import { type GetStaticPropsResult, type GetStaticPathsResult } from 'next'
import dynamic from 'next/dynamic'
import { type ParsedUrlQuery } from 'querystring'

import { pageModuleQuery } from '@data/sanity/queries/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { type SanityPage } from '@data/sanity/queries/types/page'
import { getPageSanityClient, getSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getAllSlugs,
  getPage,
} from '@lib/sanity/page'
import { getEnabledLocales } from '@lib/sanity/site'
import { parseOptionalParameters } from '@lib/helpers'
import { type Locale } from '@lib/language'
import { getNonNullValues } from '@lib/page'
import {
  type CustomStaticPropsContext,
  type StaticPathWithSlugs,
} from '@lib/routes'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getWebPageSchema } from '@lib/schema/web'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface PageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  slugs: string[]
  page: SanityPage
  site: SanitySiteFragment
}

const Page = ({
  draftMode,
  draftToken,
  locale,
  slugs,
  page,
  site,
}: PageProps) => {
  const webPageSchema = getWebPageSchema(page, site)
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [webPageSchema, ...videoObjectSchemas]

  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site} schemas={schemas} draftMode>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={pageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
              pageSlugs: slugs,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout page={page} site={site} schemas={schemas}>
      <Modules modules={page.modules} />
    </Layout>
  )
}

interface PageParams extends ParsedUrlQuery {
  slugs: string[]
}

export async function getStaticProps(
  context: CustomStaticPropsContext<PageParams>
): Promise<GetStaticPropsResult<PageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const enabledLocales = await getEnabledLocales(sanityClient)
  const slugs = parseOptionalParameters(context.params?.slugs) ?? []
  const page = await getPage(sanityClient, context.locale, slugs.join('/'))

  if (
    !page.page ||
    page.page.type === 'template' ||
    !enabledLocales.includes(context.locale)
  ) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      slugs,
      page: getNonNullValues(page.page),
      site: getNonNullValues(page.site),
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<PageParams>
> {
  const sanityClient = getSanityClient()
  const allPages = await getAllSlugs(sanityClient, 'page')
  const enabledLocales = await getEnabledLocales(sanityClient)
  const paths: StaticPathWithSlugs[] = []

  allPages
    .filter(({ slug }) => !!slug)
    .map((page) => ({
      ...page,
      locale: page.locale as Locale,
    }))
    .filter(({ locale }) => enabledLocales.includes(locale))
    .forEach(({ locale, slug }) => {
      paths.push({
        params: {
          slugs: slug.split('/').filter((slug) => !!slug),
        },
        locale,
      })
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default Page
